import { Box, Modal, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CardBody from '@/components/Card/CardBody';
import CardHeader from '@/components/Card/CardHeader';
import Button from '@/components/CustomButtons/Button';
import CustomInput from '@/components/CustomInput/CustomInput';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import PermissionsMatrix from '@/components/PermissionsMatrix/PermissionsMatrix';
import { useGetAdminPermissionSchema } from '@/api/hooks/roles/useGetAdminPermissionSchema';
import { modalStyle } from '@/views/Clients/Claim';

const AdminUserFormModal = ({
  open,
  isEdit,
  inputs,
  modalErr,
  permissions,
  onPermissionsChange,
  onInputChange,
  onClose,
  onSubmit,
  onResendActivationEmail,
  onDelete,
}) => {
  const { t } = useTranslation();
  const {
    adminPermissionSchema,
    loading: schemaLoading,
  } = useGetAdminPermissionSchema();

  const handlePermissionToggle = (
    templateKey,
    childKey,
    permission,
    checked,
  ) => {
    onPermissionsChange((prev) => {
      const current = prev[templateKey]?.[childKey] || [];

      return {
        ...prev,
        [templateKey]: {
          ...prev[templateKey],
          [childKey]: checked
            ? [...current.filter((p) => p !== permission), permission]
            : current.filter((p) => p !== permission),
        },
      };
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <form onSubmit={onSubmit}>
        <Box
          sx={modalStyle}
          className='makeStyles-card-96'
          noValidate
          autoComplete='off'>
          <CardHeader style={{ transform: 'translateY(-50%)' }} color='primary'>
            {isEdit ? (
              <h4>{t('adminUserManagement.modal.editTitle')}</h4>
            ) : (
              <h4>{t('adminUserManagement.modal.addTitle')}</h4>
            )}
          </CardHeader>
          <CardBody style={{ marginTop: '-20px' }}>
            {modalErr && (
              <p
                style={{
                  color: 'red',
                  padding: '0',
                  margin: 0,
                  fontSize: '15px',
                }}>
                {modalErr}
              </p>
            )}
            <GridContainer>
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  type='text'
                  labelText={t('adminUserManagement.modal.nameLabel')}
                  onChange={(e) => onInputChange(e, 'name')}
                  value={inputs.name}
                  autoComplete='off'
                  formControlProps={{
                    fullWidth: true,
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  type='email'
                  labelText={t('adminUserManagement.modal.emailLabel')}
                  onChange={(e) => onInputChange(e, 'email')}
                  value={inputs.email}
                  disabled={isEdit}
                  autoComplete='off'
                  formControlProps={{
                    fullWidth: true,
                  }}
                />
              </GridItem>
              <main
                style={{
                  padding: '20px',
                  gap: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}>
                {schemaLoading ? (
                  <Skeleton variant='rounded' height={200} width='100%' />
                ) : (
                  <Box sx={{ maxHeight: '40vh', overflowY: 'auto' }}>
                    <PermissionsMatrix
                      permissions={permissions}
                      onPermissionChange={handlePermissionToggle}
                      permissionSchema={adminPermissionSchema}
                      loading={schemaLoading}
                      title={t('adminUserManagement.modal.permissionsTitle')}
                    />
                  </Box>
                )}
              </main>
            </GridContainer>
            <div>
              <GridContainer>
                <Button
                  variant='contained'
                  color='primary'
                  type='submit'
                  style={{ margin: '5px 20px' }}>
                  {isEdit
                    ? t('common.buttons.update')
                    : t('common.buttons.add')}
                </Button>
                {isEdit && inputs.enabled === 'true' && (
                  <Button
                    variant='contained'
                    color='warning'
                    onClick={onResendActivationEmail}
                    style={{ margin: '5px 20px 5px 0px' }}>
                    {t('adminUserManagement.resendActivationButton')}
                  </Button>
                )}
                {isEdit && (
                  <Button variant='contained' color='danger' onClick={onDelete}>
                    {t('common.buttons.delete')}
                  </Button>
                )}
              </GridContainer>
            </div>
          </CardBody>
        </Box>
      </form>
    </Modal>
  );
};

export default AdminUserFormModal;
